import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatestWith,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	startWith,
	Subject,
	Subscription,
	take,
	withLatestFrom,
} from 'rxjs';
import { markFormGroupTouched } from '@core/helpers';
import { StepComponent } from '../interfaces/step-component';
import { FormGroup } from '@angular/forms';
import { IMenu } from '../interfaces';

@Injectable({
	providedIn: 'root',
})
export class StepMenuService {
	private changeStepSource = new Subject<number>();
	changeStep$ = this.changeStepSource.asObservable();

	formsSubject = new BehaviorSubject<FormGroup[]>([]);
	forms$ = this.formsSubject.asObservable();

	instancesSubject = new BehaviorSubject<Record<string, StepComponent>>({});
	instances$ = this.instancesSubject.asObservable();

	formsChangeSubject = new BehaviorSubject<void>(undefined);
	formsChange$ = this.formsChangeSubject.asObservable();

	formValidSubject = new BehaviorSubject<boolean>(true);
	formValid$ = this.formValidSubject.asObservable();

	previusInstancesValidSubject = new BehaviorSubject<boolean>(false);
	previusInstancesValid$ = this.previusInstancesValidSubject.asObservable();

	private validSubscription?: Subscription;

	constructor() {
		this.changeStep$
			.pipe(
				startWith(0),
				combineLatestWith(this.formsChange$),
				withLatestFrom(this.instances$),
				filter(([_, instances]) => !!instances),
				map(([[step, _], instances]) => ({ instance: instances[step] as StepComponent, step }))
			)
			.subscribe(({ instance }) => {
				// TODO: validate inmediate formGroup
				// markFormGroupTouched(instance.formGroup, true);
				this.validSubscription?.unsubscribe();

				if (instance?.formGroup) {
					this.validSubscription = instance.formGroup.valueChanges
						.pipe(
							startWith(null),
							distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
						)
						.subscribe(() => this.formValidSubject.next(instance.formGroup.valid));
				} else {
					this.formValidSubject.next(true);
				}
			});
	}

	changeStep(step: number): void {
		this.changeStepSource.next(step);
	}

	registerForm(instance: StepComponent, stepId: number): void {
		const currentInstances = this.instancesSubject.getValue();
		this.instancesSubject.next({ ...currentInstances, [stepId]: instance });

		// Solo suscribir al valueChanges de la nueva instancia (una sola vez)
		if (instance?.formGroup) {
			instance.formGroup.valueChanges.subscribe(() => this.formsChangeSubject.next());
		}
	}

	unregisterForm(id: number): void {
		const { [id]: _, ...rest } = this.instancesSubject.getValue();
		this.instancesSubject.next(rest);
	}

	validatePreviousInstances<T>(prevStep: number, isDecreasing: boolean, routeItems: IMenu<T>[]): Observable<boolean> {
		return this.instances$.pipe(
			map((instances) => {
				return Object.values(instances)
					.filter((_, index) => index <= prevStep)
					.map((instance, index) => {
						const routeItem = routeItems?.[index];
						
						if (isDecreasing) return true;
						
						if (routeItem?.validateInstance) {
							return routeItem.validateInstance();
						} else if (instance?.validateInstance?.() !== undefined) {
							return instance.validateInstance();
						} else if (instance?.formGroup) {
							markFormGroupTouched(instance.formGroup, true);

							return instance.formGroup.valid;
						}

						return true;
					})
					.reduce((acc, valid) => {
						if (!valid) return valid;

						return acc;
					}, true);
			}),
			take(1)
		);
	}
}
